import { applyDatabinding } from '@dynamic/ui';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { usePageLayout } from '#/client/custom';
import { LayoutData, PageLayout } from '#/client/models';
import { cancelOldRequests, DEFAULT_PAGE_SIZE } from '#/dynamic/dynui-fetchers';
import { Errors } from '#/errors/errors';
import { OneError } from '#/errors/oneError';
import { useConfig } from '#/hooks/useConfig';
import { useParentalSystemForItem } from '#/hooks/useParentalSystemForItem';
import { useResponsiveFormFactor } from '#/hooks/useResponsiveValue';
import { useSeoInfo } from '#/hooks/useSeoInfo';
import { Path } from '#/service/routes';
import { useTranslationStore } from '#/store/translation/translation.store';

import { PageControllerProps } from './pageTypes';
import { useSendPageScreenView } from './useSendPageScreenView.hook';

export function usePageController({
  defaultContext = {},
  pageId,
  customPageTitle,
  cacheKey,
  screenType,
  screenTitle,
}: PageControllerProps) {
  const location = useLocation();
  const [v2Templates, setV2Templates] = useState<any>();
  const [layout, setLayout] = useState<LayoutData>();
  const { theme } = useConfig();
  const item = defaultContext.item;
  const selectedParentalSystem = useParentalSystemForItem(item);
  const { translations, locale } = useTranslationStore();
  const formFactor = useResponsiveFormFactor();
  const { error, data: pageLayout } = usePageLayout(pageId);

  useSendPageScreenView({
    pageLayout,
    pageId,
    screenType,
    item,
    formFactor,
    screenTitle,
  });

  validatePageLayout(pageLayout, pageId);
  useSeoInfo({
    title: customPageTitle,
    localizedTitle: pageLayout?.page.templates?.[formFactor].properties.title,
    item,
  });

  useEffect(() => {
    return cancelRequestIfNeeded(location.pathname);
  }, [location.pathname]);

  // Clear current templates between different pages.
  useEffect(() => {
    setV2Templates(undefined);
  }, [pageId]);

  useEffect(() => {
    const shouldLoad = pageLayout && !v2Templates;

    if (!shouldLoad) {
      return;
    }

    const collections = pageLayout.page.containerCollections;

    if (!collections) {
      throw new Error(
        'PageController - containerCollections not found in page layout.'
      );
    }

    // The only remaining reasons why we have to manipulate the layout:
    // - Data binding the `feedUrl`: This could be moved to DynUi in the future.
    // - Passing `collectionId`: Since we use `feedUrl`, `collectionId` shouldn't be needed.
    // - Passing the `pageSize`: We could just rely on the DynUi-side default (also 20).
    // Resolving all of these would mean we can switch back to using the original `feed` and `data` instead of
    // `feedUrl` and `dataSource` in the containers, and we could finally remove this code.

    collections.forEach((collection) =>
      collection.containers.forEach((container) => {
        // ignore null/wrong containers
        if (!container) {
          return;
        }

        const originalFeed = container.data.feed ?? '';

        // URI decoded & databound result
        const databoundFeed = getFeedEndpoint(originalFeed, defaultContext);

        const { resourceId } = getResourceIdAndType(originalFeed);

        // merge container data source into the layout
        container.dataSource = {
          feedUrl: databoundFeed,
          collectionId: resourceId,
          pageSize: DEFAULT_PAGE_SIZE,
        };
      })
    );

    console.debug('PageController - Page fetched', pageLayout.page);
    setLayout(pageLayout.page);
    const { pages, containers: pageContainers, items } = pageLayout.templates;
    setV2Templates({ ...pages, ...pageContainers, ...items });
  }, [cacheKey, defaultContext, locale, pageLayout, v2Templates]);

  return {
    layout,
    error,
    theme,
    translations,
    parentalSystem: selectedParentalSystem,
    v2Templates,
    defaultContext,
  };
}

const getFeedEndpoint = (feed: string, context: object) =>
  applyDatabinding({
    value: decodeURIComponent(feed),
    context,
  });

const getResourceIdAndType = (feedUrl: string) => {
  const feedUrlArr = feedUrl.split('/');
  const resourceId = feedUrlArr[feedUrlArr.length - 1];
  const resourceType = feedUrlArr[feedUrlArr.length - 2];

  return { resourceId, resourceType };
};

function validatePageLayout(data?: PageLayout, pageId?: string) {
  if (!data) {
    return;
  }

  const pageLayout = data.page;
  const pageTemplates = data.templates;

  if (!pageLayout) {
    throw new OneError(Errors.CONFIGURATION_GENERAL_MISSING_PAGE, {
      extras: { pageId },
    });
  }

  if (!pageTemplates) {
    throw new OneError(Errors.CONFIGURATION_GENERAL_MISSING_PAGE_TEMPLATE, {
      extras: { pageId },
    });
  }
}

const cancelRequestIfNeeded = (pathname: string) => {
  // Only cancel requests if we were not on the search page
  // because search page replaces the search query for each input change.
  // In such cases it could happen that it fires the request right before the return
  // of this hook would be called so that search request would be immediately canceled.
  if (pathname !== Path.Search) {
    cancelOldRequests();
  }
};
